export type SearchAttribution = {
  sessionId?: string;
  userToken?: string;
  platform?: "web" | "app";
  query?: string;
  queryId?: string;
  productId?: string;
  position?: number;
  pageContext?: string;
  isSponsored?: boolean;
  placement?: string;
  storedAt?: number;
};

const STORAGE_KEY = "lealti_search_attribution_v1";
const TTL_MS = 1000 * 60 * 60 * 24 * 7;

const getStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const loadStore = (): Record<string, SearchAttribution> => {
  const storage = getStorage();
  if (!storage) return {};
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as Record<string, SearchAttribution>;
    }
  } catch {
    return {};
  }
  return {};
};

const saveStore = (store: Record<string, SearchAttribution>) => {
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(STORAGE_KEY, JSON.stringify(store));
};

const normalizeKey = (value: string, prefix = "id") => `${prefix}:${value}`;

export const saveSearchAttribution = (
  attribution: SearchAttribution & { productId?: string; productSlug?: string }
) => {
  if (!attribution) return;
  const now = Date.now();
  const store = loadStore();
  const entry: SearchAttribution = { ...attribution, storedAt: now };

  if (attribution.productId) {
    store[normalizeKey(attribution.productId, "id")] = entry;
  }
  if (attribution.productSlug) {
    store[normalizeKey(attribution.productSlug, "slug")] = entry;
  }

  saveStore(store);
};

export const getSearchAttribution = (productId?: string, productSlug?: string) => {
  const store = loadStore();
  const now = Date.now();
  const keys = [
    productId ? normalizeKey(productId, "id") : null,
    productSlug ? normalizeKey(productSlug, "slug") : null,
  ].filter(Boolean) as string[];

  for (const key of keys) {
    const entry = store[key];
    if (!entry) continue;
    if (entry.storedAt && now - entry.storedAt > TTL_MS) {
      delete store[key];
      saveStore(store);
      continue;
    }
    return entry;
  }

  return null;
};

export const clearSearchAttribution = (productId?: string, productSlug?: string) => {
  const store = loadStore();
  let changed = false;
  if (productId) {
    const key = normalizeKey(productId, "id");
    if (store[key]) {
      delete store[key];
      changed = true;
    }
  }
  if (productSlug) {
    const key = normalizeKey(productSlug, "slug");
    if (store[key]) {
      delete store[key];
      changed = true;
    }
  }
  if (changed) saveStore(store);
};
